
import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useWindowSize } from "react-use";
import Confetti from "react-confetti";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { decryptToken } from "../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../components/BaseUrl.js";
import { logout } from "../../../Redux/Slices/AuthSlice";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const formatLabel = (value) => {
  if (!value) return "-";
  return String(value)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatStartTime = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const PaymentSuccess = () => {
  const { state } = useLocation(); // populated only when reached via an in-app navigate (e.g. subscription activation)
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { width, height } = useWindowSize();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [showConfetti, setShowConfetti] = useState(true);
  const { t } = useTranslation();
  const token = useSelector((s) => s.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  // PayPal redirects straight here (no router state) for one-time order
  // payments, e.g. the worker top-up flow — capture the order ourselves.
  const orderToken = searchParams.get("token");
  // upgrade_token can be lost across the PayPal redirect round-trip, so also
  // treat "reached via in-app state but with no real logged-in session" as
  // an upgrade-flow signal, not just the explicit flag.
  const upgradeFlow = Boolean(state?.upgradeFlow) || (Boolean(state) && !token);
  const [capturing, setCapturing] = useState(Boolean(!state && orderToken));
  const [captureData, setCaptureData] = useState(null);

  // Reached via the blocked-account email upgrade link: the admin's old session
  // token is often still unexpired in storage, which makes PublicRoute bounce "/"
  // straight back to /dashboard. Clear it so they land on the real login screen.
  const goToNextScreen = () => {
    if (upgradeFlow) {
      dispatch(logout());
      navigate("/", { replace: true });
    } else {
      navigate("/subscription-plans");
    }
  };

  useEffect(() => {
    if (state || !orderToken) return;

    const capturePayment = async () => {
      try {
        const res = await fetch(`${BASE_URL}/payments/capture-payment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
          body: JSON.stringify({ order_id: orderToken }),
        });

        const json = await res.json();
        if (!res.ok || json.error) {
          throw new Error(json.message || t("alerts.update_error"));
        }

        setCaptureData(json.data || {});
      } catch (err) {
        console.error("Error capturing payment:", err);
        toast.error(err.message || t("alerts.update_error"));
        navigate("/subscription-plans");
      } finally {
        setCapturing(false);
      }
    };

    capturePayment();
  }, [state, orderToken, decryptedToken, navigate, t]);

  useEffect(() => {
    if (capturing) return;

    if (!state && !captureData) {
      // No subscription-activation state and no order to capture — nothing to show here
      goToNextScreen();
      return;
    }

    // auto redirect after 9 seconds if user doesn't click back
    const timer = setTimeout(() => {
      goToNextScreen();
    }, 5000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, captureData, capturing, upgradeFlow, navigate]);

  const backPlans = () => goToNextScreen();

  if (capturing) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // One-time order payment receipt (reached directly from PayPal, no router state)
  if (!state && captureData) {
    const isWorkerTopup = captureData.payment_type === "worker_topup";

    const rawRows = isWorkerTopup
      ? [
          { label: t("subscriptionPage.new_worker_count"), value: captureData.new_worker_count },
          { label: t("payment_receipt.order_id"), value: captureData.order_id ?? orderToken },
          { label: t("subscriptionPage.payment_id"), value: captureData.payment_id },
          { label: t("subscriptionPage.seat_status"), value: captureData.seat_change_status && formatLabel(captureData.seat_change_status) },
        ]
      : [
          { label: t("payment_receipt.order_id"), value: captureData.order_id ?? orderToken },
          { label: t("payment_receipt.status"), value: captureData.status },
          { label: t("payment_receipt.amount"), value: captureData.amount },
          { label: t("payment_receipt.transaction_id"), value: captureData.txn_id },
        ];

    // Only show rows the API actually returned a value for
    const rows = rawRows.filter(
      (row) => row.value !== undefined && row.value !== null && row.value !== ""
    );

    const needsRevisionApproval =
      isWorkerTopup && captureData.revise_success === false && captureData.subscription_revision_approval_url;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          py: 5,
        }}
      >
        {showConfetti && <Confetti width={width} height={height} />}

        <CheckCircleIcon
          sx={{ fontSize: isMobile ? 100 : 140, color: "#4caf50", mb: 2 }}
        />
        <Typography
          variant="h4"
          sx={{
            fontSize: isMobile ? "35px" : "45px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: "bold",
            mb: 1,
          }}
        >
          {isWorkerTopup ? t("subscriptionPage.topup_payment_success_title") : t("payment_success.title")}
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 4, fontFamily: "Poppins, sans-serif", color: "#5E5C5C",maxWidth:"600px" }}
        >
          {isWorkerTopup ? t("subscriptionPage.topup_payment_success_desc") : t("payment_success.message")}
        </Typography>

        <Box
          sx={{
            bgcolor: "#F7F7F7",
            borderRadius: 2,
            py: 6,
            px: 4,
            mb: 3,
            width: "100%",
            maxWidth: 450,
          }}
        >
          {rows.map((row, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: i === rows.length - 1 ? 0 : 2,
              }}
            >
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 16,
                  fontWeight: 400,
                  color: "#5E5C5C",
                }}
              >
                {row.label}
              </CustomText>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 15,
                  fontWeight: 400,
                  color: "#172B4D",
                  maxWidth: "60%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={String(row.value)}
              >
                {row.value}
              </CustomText>
            </Box>
          ))}
        </Box>

        {needsRevisionApproval && (
          <Box sx={{ mb: 3, maxWidth: 450 }}>
            <Typography sx={{ fontSize: 13, color: "#d32f2f", mb: 1.5, fontFamily: "Poppins, sans-serif" }}>
              {t("subscriptionPage.subscription_update_pending")}
            </Typography>
            <CustomButton
              size="medium"
              label={t("subscriptionPage.approve_subscription_update")}
              onClick={() => {
                window.location.href = captureData.subscription_revision_approval_url;
              }}
            />
          </Box>
        )}

        <CustomButton size="medium" label={t("subscriptionPage.back_to_plans")} onClick={backPlans} />
      </Box>
    );
  }

  if (!state) return null; // redirecting to /subscription-plans

  const details = state;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
        py: 5,
      }}
    >
      {showConfetti && <Confetti width={width} height={height} />}

      <CheckCircleIcon
        sx={{ fontSize: isMobile ? 100 : 140, color: "#4caf50", mb: 2 }}
      />
      <Typography
        variant="h4"
        sx={{
          fontSize: isMobile ? "35px" : "45px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: "bold",
          mb: 1,
        }}
      >
  {t("subscriptionPage.payment_successful")}      </Typography>
      <Typography
        variant="body1"
        sx={{ mb: 4, fontFamily: "Poppins, sans-serif", color: "#5E5C5C" }}
      >
     {t("subscriptionPage.subscription_activated")}
      </Typography>

      <Box
        sx={{
          bgcolor: "#F7F7F7",
          borderRadius: 2,
          py: 6,
          px: 4,
          mb: 3,
          width: "100%",
          maxWidth: 450,
        }}
      >
        {[
          { label:  t("subscriptionPage.subscription_id"), value: details.subscription_id },
          { label: t("subscriptionPage.plan_id"), value: formatLabel(details.plan_id) },
          { label: t("subscriptionPage.company_id"), value: details.company_id },
          { label:  t("subscriptionPage.worker_count"), value: details.worker_count },
          { label: t("subscriptionPage.per_worker_amount"), value: `$${details.per_worker_amount}` },
          { label: t("subscriptionPage.total_amount"), value: `$${details.total_amount}` },
          { label: t("subscriptionPage.status"), value: details.status },
          { label:  t("subscriptionPage.start_time"), value: formatStartTime(details.start_time) },
        ].map((row, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: i === 7 ? 0 : 2,
            }}
          >
            <CustomText
              sx={{
                ...textStyles.h1,
                fontSize: 16,
                fontWeight: 400,
                color: "#5E5C5C",
              }}
            >
              {row.label}
            </CustomText>
            <CustomText
              sx={{
                ...textStyles.h1,
                fontSize: 15,
                fontWeight: 400,
                color: "#172B4D",
                maxWidth: "60%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={String(row.value)}
            >
              {row.value}
            </CustomText>
          </Box>
        ))}
      </Box>

      <CustomButton size="medium" label={t("subscriptionPage.back_to_plans")}  onClick={backPlans} />
    </Box>
  );
};

export default PaymentSuccess;
